// undefined is not included here since Array.prototype.sort does treat undefined array entries
// like other entries. undefined entries will always be sorted last. This likely doesn't affect
// sorting array of objects that have undefined sort keys but it does affect array where the
// element itself is the sort key. Having it work in one scenario but not the other would produce
// inconsistent sorts.
export type Comparable = string | number | null;
export type Comparator<T> = (a: T, b: T) => number;
export type Sensitivity = 'base' | 'accent' | 'strict';

/**
 * Common options for comparators
 */
export interface ComparatorOptions<T> {
  /**
   * Specifies ascending or descending sort.
   */
  direction?: 'ascending' | 'descending';

  /**
   * Letters can compared using multiple traits. This option sets which traits should be used when
   * comparing characters. The default is 'strict'.
   *
   * - base - only the base character is compared ( a != b, a == á, a == A )
   * - accent - accent marks are used to distinguish character (a != b, a != á, a == A)
   * - strict - both accent and case differences are used (a != b, a != á, a != A)
   */
  sensitivity?: Sensitivity;

  /**
   * A function to convert T to a comparable type.
   */
  toSortKey?: (t: T) => Comparable;
}

export type ComparatorFactory = <T>(
  locale: string,
  options?: ComparatorOptions<T>
) => Comparator<T>;

export const addDefaults = <T>(o?: ComparatorOptions<T>): Required<ComparatorOptions<T>> => {
  return {
    direction: 'ascending',
    sensitivity: 'strict',
    toSortKey: <T>(t: T) => (typeof t === 'number' ? t : String(t)),
    ...o,
  };
};
